import { isStaff, isSuperadmin } from "./roles";
import { LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import { Route, Switch, useLocation } from "wouter";
import { ApiError, dashboardApi } from "./api";
import { setServiceCatalogue } from "./service-text";
import { useT } from "./i18n";
import AppShell from "./components/AppShell";
import NewRequestPage from "./pages/NewRequestPage";
import SearchPage from "./pages/SearchPage";
import ActivityPage from "./pages/ActivityPage";
import AdminPage from "./pages/AdminPage";
import PortfolioPage from "./pages/PortfolioPage";
import CatalogPage from "./pages/CatalogPage";
import GalleryPage from "./pages/GalleryPage";
import DeliveryPage from "./pages/DeliveryPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import LedgerPage from "./pages/LedgerPage";
import LoginPage from "./pages/LoginPage";
import OperationsPage from "./pages/OperationsPage";
import OverviewPage from "./pages/OverviewPage";
import StatementPage from "./pages/StatementPage";
import RequestsPage from "./pages/RequestsPage";
import RelationshipPage from "./pages/RelationshipPage";
import ReviewPage from "./pages/ReviewPage";
import MonthReviewPage from "./pages/MonthReviewPage";
import SharePage from "./pages/SharePage";
import type { AccountSummary, DashboardData, Service, SessionUser, UserRole } from "./types";
import { contractMonths, monthLabel } from "./utils";

function currentMonth() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export default function App() {
  const t = useT();
  const [, navigate] = useLocation();
  // approvalThreshold null = no escalation ceiling (BM removed it 2026-08-18). Do not
  // default it to a number here — that would flash a stale limit before /api/config lands.
  const [config, setConfig] = useState<{ demoEnabled: boolean; monthlyCredits: number; creditValueKd: number; approvalThreshold: number | null }>({ demoEnabled: false, monthlyCredits: 69, creditValueKd: 50, approvalThreshold: null });
  const [user, setUser] = useState<SessionUser | null>(null);
  // Only ever populated for staff — the server does not send it to clients, so
  // there is nothing for a client UI to reveal or a client to guess at.
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState<DashboardData | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  // Set when the server refuses the workspace for a reason worth showing. Without
  // these the only outcome of a failed load was `data` staying null forever, which
  // renders as a spinner that never resolves.
  const [opensOn, setOpensOn] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [booting, setBooting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    Promise.all([dashboardApi.config(), dashboardApi.session()])
      .then(([nextConfig, session]) => {
        setConfig(nextConfig);
        setUser(session.user);
        setAccounts(session.accounts ?? []);
      })
      .catch(() => setAuthError(t("error.unreachable")))
      .finally(() => setBooting(false));
  }, []);

  const loadWorkspace = useCallback(async () => {
    if (!user) return;
    // Nothing to load while the account is locked behind the password change —
    // every data route refuses it, so asking is pure noise.
    if (user.mustChangePassword) return;
    setLoading(true);
    try {
      const [nextData, serviceData] = await Promise.all([dashboardApi.dashboard(month), dashboardApi.services()]);
      setData(nextData);
      setServices(serviceData.services);
      setCategories(serviceData.categories);
      // Feeds the Arabic name of every service to the screens that show one —
      // requests, the ledger, search, the gallery — without threading the whole
      // catalogue through their props. See src/service-text.ts.
      setServiceCatalogue(serviceData.services, serviceData.categoriesAr);
      setOpensOn(null);
      setLoadFailed(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
        setData(null);
        return;
      }
      // Don't shout about the password gate. The full-screen change form is
      // already explaining it far better than a red toast in the corner can.
      if (error instanceof ApiError && error.status === 403) return;
      // A contract that has not started yet still has a workspace worth opening —
      // the client is onboarded, they just cannot spend until day one. Snap the
      // WHOLE app to the contract's first month rather than refusing.
      //
      // This has to happen here and not server-side: every sub-page (gallery,
      // review, month-review) is handed this same `month` state, so quietly
      // serving another month from /api/dashboard would leave the picker reading
      // today while the numbers came from somewhere else, and every other route
      // would still ask for — and be refused — the pre-contract month.
      //
      // Guarded on inequality so a first month that itself fails falls through to
      // the explain-screen below instead of reloading forever.
      if (error instanceof ApiError && typeof error.detail.contractStart === "string") {
        const firstMonth = error.detail.contractStart.slice(0, 7);
        if (firstMonth !== month) {
          setMonth(firstMonth);
          return;
        }
        setOpensOn(error.detail.contractStart);
        return;
      }
      setLoadFailed(true);
      toast.error(error instanceof Error ? error.message : t("error.loadDashboard"));
    } finally {
      setLoading(false);
    }
  }, [month, user]);

  useEffect(() => { void loadWorkspace(); }, [loadWorkspace]);

  async function login(email: string, password: string) {
    setAuthBusy(true);
    setAuthError("");
    try {
      const result = await dashboardApi.login(email, password);
      setUser(result.user);
      // Re-read the session AFTER signing in. The boot-time read happens while
      // nobody is authenticated, so it can never carry the account list — without
      // this the switcher is missing for the whole session and staff silently lose
      // access to every client but their own.
      const session = await dashboardApi.session();
      setAccounts(session.accounts ?? []);
      navigate("/");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : t("error.signIn"));
    } finally {
      setAuthBusy(false);
    }
  }

  async function preview(role: UserRole) {
    setAuthBusy(true);
    setAuthError("");
    try {
      const result = await dashboardApi.preview(role);
      setUser(result.user);
      const session = await dashboardApi.session();   // same reason as login()
      setAccounts(session.accounts ?? []);
      navigate("/");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : t("error.openPreview"));
    } finally {
      setAuthBusy(false);
    }
  }

  async function switchAccount(accountId: string) {
    setLoading(true);
    try {
      const result = await dashboardApi.switchAccount(accountId);
      // Replacing the user re-runs loadWorkspace, which reloads the dashboard AND
      // the service catalogue. Both matter: rates differ per client, so a stale
      // catalogue would price the new client's work at the previous one's rates.
      setUser(result.user);
      setData(null);
      toast.success(t("toast.switchedTo", { name: result.account.name }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.switchAccount"));
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    try { await dashboardApi.logout(); } catch { /* Local sign-out still clears the UI. */ }
    setUser(null);
    setData(null);
    navigate("/");
  }

  // A route, not a flag. See NewRequestPage for why the modal went away.
  function openRequest(serviceId?: string) {
    navigate(serviceId ? `/request/new?service=${encodeURIComponent(serviceId)}` : "/request/new");
  }

  const months = useMemo(() => data ? contractMonths(data.account.contractStart, data.account.contractEnd) : [month], [data, month]);

  // THE SHARE PAGE RENDERS BEFORE EVERYTHING — before booting, before the auth
  // gate, outside the shell. Somebody opening a shared link is not a user of this
  // system: they must not be shown a sign-in screen, a loading state that implies
  // one, or any navigation into the product. Checked on the raw path rather than
  // through the router so it cannot be reordered by accident later.
  // Any non-empty token, not a length-checked one. A link truncated by an email
  // client is EXACTLY how this goes wrong, and a minimum length here sent that
  // reader to a sign-in screen — the one page they must never be shown. The
  // server decides what is valid; the page just reports it.
  const sharePath = /^\/s\/([A-Za-z0-9_-]+)\/?$/.exec(window.location.pathname);
  if (sharePath) return <SharePage token={sharePath[1]} />;

  if (booting) return <LoadingScreen label="Opening production credits" />;
  if (!user) return <><LoginPage demoEnabled={config.demoEnabled} onLogin={login} onPreview={preview} busy={authBusy} error={authError} /><Toaster position="top-right" richColors /></>;
  // Before the workspace loads, not inside it. The server refuses every other
  // route while this flag is set, so rendering the app first would just show a
  // screen full of permission errors.
  if (user.mustChangePassword) {
    return (
      <>
        <ChangePasswordPage
          user={user}
          onChanged={(next) => { setUser(next); toast.success(t("toast.passwordUpdated")); }}
          onLogout={() => void logout()}
        />
        <Toaster position="top-right" richColors />
      </>
    );
  }
  // The client's own initial. This screen was hardcoded to "Z", so the second
  // client onboarded was shown the first client's brand mark. "P" when nobody is
  // signed in yet — the same fallback, and the same reason, as LoginPage.
  const brandLetter = user.displayName.trim().charAt(0).toUpperCase() || "P";
  if (!data && opensOn) {
    return (
      <WorkspaceMessage
        brand={brandLetter}
        title={t("workspace.opensTitle", { month: monthLabel(opensOn.slice(0, 7)) })}
        body={t("workspace.opensBody")}
        signOutLabel={t("shell.signOut")}
        onSignOut={() => void logout()}
      />
    );
  }
  if (!data && loadFailed) {
    return (
      <WorkspaceMessage
        brand={brandLetter}
        title={t("workspace.failedTitle")}
        body={t("workspace.failedBody")}
        actionLabel={t("workspace.retry")}
        onAction={() => void loadWorkspace()}
        signOutLabel={t("shell.signOut")}
        onSignOut={() => void logout()}
      />
    );
  }
  if (!data) return <LoadingScreen label="Loading client workspace" brand={brandLetter} />;

  return (
    <>
      <AppShell
        user={user}
        month={month}
        months={months}
        available={data.balance.available}
        total={data.balance.totalPool}
        onMonthChange={setMonth}
        onNewRequest={() => openRequest()}
        onLogout={() => void logout()}
        accountName={data.account.name}
        accounts={accounts}
        onSwitchAccount={(id: string) => void switchAccount(id)}
      >
        {loading && <div className="page-loading"><LoaderCircle size={17} className="spin" /> {t("shell.updating")}</div>}
        <Switch>
          <Route path="/"><OverviewPage data={data} user={user} onNewRequest={() => openRequest()} /></Route>
          <Route path="/requests"><RequestsPage requests={data.requests} user={user} available={data.balance.available} approvalThreshold={config.approvalThreshold} onNewRequest={() => openRequest()} onChanged={loadWorkspace} /></Route>
          <Route path="/request/new">
            <NewRequestPage
              services={services}
              month={month}
              onCreated={() => { void loadWorkspace(); navigate("/requests"); }}
              // Back to wherever they came from when there is somewhere to go back
              // to; a deep link opened cold has no history, and sending that reader
              // out of the app is worse than sending them to the overview.
              onCancel={() => { if (window.history.length > 1) window.history.back(); else navigate("/"); }}
            />
          </Route>
          <Route path="/services"><CatalogPage services={services} categories={categories} onRequest={openRequest} account={data.account} canPrice={isSuperadmin(user.role)} onRateChanged={loadWorkspace} /></Route>
          <Route path="/work"><GalleryPage month={month} /></Route>
          <Route path="/search"><SearchPage /></Route>
          <Route path="/delivery/:id">{(params) => <DeliveryPage requestId={params.id} />}</Route>
          <Route path="/statement"><StatementPage data={data} user={user} onChanged={loadWorkspace} /></Route>
          <Route path="/ledger"><LedgerPage data={data} /></Route>
          {/* Client-readable, staff-writable — the server enforces it, the page
              hides the controls that would 403. */}
          <Route path="/relationship"><RelationshipPage user={user} requests={data.requests} /></Route>
          <Route path="/review"><ReviewPage month={month} /></Route>
          {/* The client's month, work-first. Shares the quarterly assembler so the
              two can never disagree about the same weeks. */}
          <Route path="/month-review"><MonthReviewPage month={month} /></Route>
          <Route path="/operations">{isStaff(user.role) ? <OperationsPage data={data} services={services} user={user} onChanged={loadWorkspace} /> : <OverviewPage data={data} user={user} onNewRequest={() => openRequest()} />}</Route>
          <Route path="/portfolio">{isSuperadmin(user.role) ? <PortfolioPage month={month} /> : <OverviewPage data={data} user={user} onNewRequest={() => openRequest()} />}</Route>
          <Route path="/administration">{isSuperadmin(user.role) ? <AdminPage /> : <OverviewPage data={data} user={user} onNewRequest={() => openRequest()} />}</Route>
          <Route path="/activity">{isStaff(user.role) ? <ActivityPage /> : <OverviewPage data={data} user={user} onNewRequest={() => openRequest()} />}</Route>
          <Route><OverviewPage data={data} user={user} onNewRequest={() => openRequest()} /></Route>
        </Switch>
      </AppShell>

      <Toaster position="top-right" richColors closeButton />
    </>
  );
}

function LoadingScreen({ label, brand = "P" }: { label: string; brand?: string }) {
  return <main className="loading-screen"><div className="loading-brand">{brand}</div><LoaderCircle size={22} className="spin" /><span>{label}</span></main>;
}

/** The workspace could not be shown, and we know why. Says so, and offers the only
 *  two moves that exist from here — try again, or sign out. */
function WorkspaceMessage({ brand, title, body, actionLabel, onAction, signOutLabel, onSignOut }: {
  brand: string; title: string; body: string;
  actionLabel?: string; onAction?: () => void;
  signOutLabel: string; onSignOut: () => void;
}) {
  return (
    <main className="loading-screen">
      <div className="loading-brand">{brand}</div>
      <strong className="workspace-message-title">{title}</strong>
      <p className="workspace-message-body">{body}</p>
      <div className="workspace-message-actions">
        {actionLabel && <button type="button" className="button button-primary" onClick={onAction}>{actionLabel}</button>}
        <button type="button" className="button button-secondary" onClick={onSignOut}>{signOutLabel}</button>
      </div>
    </main>
  );
}
